import * as React from 'react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import MyButton from './buttons'
import MySelect from './MySelect'
import MyInput from './MyInput'
import { IoMdHelp } from 'react-icons/io'
import { FaUser, FaEnvelope } from 'react-icons/fa'
import MyTextArea from './MyTextArea'
import CompanyInfoModalContent from './CompanyInfoModal'
import { contactService } from '../../services/contactService'
import AuthService from '../../services/login'
import Modal from './MyModal'
import CookieManager from '../lib/CookiesManager'

type SubmissionStatus = 'idle' | 'success' | 'error'

const ContactFormAuthenticated: React.FC = () => {
  const { t } = useTranslation()
  const [formErrors] = useState<{ [key: string]: string }>({})
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(() => {
    const saved = localStorage.getItem('lastContactSubmitTime')
    return saved ? parseInt(saved) : 0
  })
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [canSubmit, setCanSubmit] = useState<boolean>(true)
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>('idle')

  const [formData, setFormData] = useState({
    firstname: '',
    title: '',
    description: '',
    email: '',
  })

  const subjectOptions = [
    { value: 'question', label: t('subject-question') },
    { value: 'suggestion', label: t('subject-suggestion') },
    { value: 'problem', label: t('subject-problem') },
    { value: 'other', label: t('subject-other') },
  ]

  const handleChange = (field: string) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubjectChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
    }))
  }

  useEffect(() => {
    const checkCooldown = () => {
      const now = Date.now()
      const timePassed = now - lastSubmitTime
      const cooldownPeriod = 5 * 60 * 1000

      if (timePassed < cooldownPeriod) {
        setCanSubmit(false)
        setTimeRemaining(Math.ceil((cooldownPeriod - timePassed) / 1000))
      } else {
        setCanSubmit(true)
        setTimeRemaining(0)
      }
    }

    const timer = setInterval(checkCooldown, 1000)
    checkCooldown()

    return () => clearInterval(timer)
  }, [lastSubmitTime])

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false)
    if (submissionStatus === 'success') {
      setFormData({
        firstname: '',
        title: '',
        description: '',
        email: '',
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    console.log('formData', formData)

    if (
      !formData.firstname.trim() ||
      !formData.title.trim() ||
      !formData.description.trim() ||
      !formData.email.trim()
    ) {
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
      return
    }

    if (!canSubmit) {
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
      return
    }

    try {
      const token = AuthService.getToken()
      const userId = CookieManager.getUserId()
      if (!token || !userId) {
        throw new Error('Not authenticated')
      }

      await contactService.sendAuthContactForm(
        {
          ...formData,
          title: `(sender-user: ${userId}) ${formData.title}`,
          userId,
        },
        token,
      )

      const now = Date.now()
      setLastSubmitTime(now)
      localStorage.setItem('lastContactSubmitTime', now.toString())

      setSubmissionStatus('success')
      setIsStatusModalOpen(true)

      setFormData({
        firstname: '',
        title: '',
        description: '',
        email: '',
      })
    } catch (error) {
      console.error('Error sending form:', error)
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
    }
  }

  return (
    <div className='w-full mx-auto justify-center bg-white mt-5'>
      <div className='mx-auto px-4 flex flex-col lg:flex-row gap-5'>
        <div className='w-full lg:w-2/3 flex flex-col'>
          <form onSubmit={handleSubmit} className='space-y-5 flex-grow'>
            <div className='flex flex-col md:flex-row gap-5'>
              <div className='w-full md:w-1/2'>
                <MyInput
                  label={t('name-firstname-nickname')}
                  value={formData.firstname}
                  onChange={handleChange('firstname')}
                  className='text-anthracite bg-grisclair'
                  icon={<FaUser />}
                  required
                  error={formErrors['name']}
                  autoComplete='name'
                />
              </div>
              <div className='w-full md:w-1/2'>
                <MyInput
                  label={t('email')}
                  value={formData.email}
                  onChange={handleChange('email')}
                  type='email'
                  className='text-anthracite bg-grisclair'
                  icon={<FaEnvelope />}
                  required
                  autoComplete='email'
                />
              </div>
            </div>

            <MySelect
              label={t('subject')}
              options={subjectOptions}
              value={formData.title}
              onChange={handleSubjectChange}
              required
              icon={<IoMdHelp />}
              className='text-anthracite bg-grisclair'
            />

            <MyTextArea
              variant='grisclair'
              label={t('message')}
              multiline
              name='message'
              value={formData.description}
              onChange={(value: string) => handleChange('description')(value)}
              required
              placeholder={t('message')}
              InputProps={{
                style: {
                  height: 'calc(3 * 80px + 2 * 16px)',
                  alignItems: 'flex-start',
                },
              }}
            />

            <div className='flex justify-center'>
              <MyButton
                variant='redpink-anthracite'
                className='w-full md:w-1/3 transition-all p-2'
                type='submit'
                disabled={!canSubmit}
              >
                {canSubmit
                  ? t('send')
                  : `${t('send')} (${Math.floor(timeRemaining / 60)}:${(
                      timeRemaining % 60
                    )
                      .toString()
                      .padStart(2, '0')})`}
              </MyButton>
            </div>
          </form>
        </div>

        <div className='w-full lg:w-1/3'>
          <div className='bg-grisclair rounded-lg overflow-hidden h-full'>
            <CompanyInfoModalContent title='Sexagogo' onClose={() => {}} />
          </div>
        </div>
      </div>
      <Modal isOpen={isStatusModalOpen} onClose={handleCloseStatusModal}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('submission-status')}
          </h2>
          <div className='space-y-4 p-5'>
            <p
              className={`text-center rounded-lg p-2 mb-4 ${
                submissionStatus === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-redlight text-red-800'
              }`}
            >
              {submissionStatus === 'success'
                ? t('contact-form-success')
                : !canSubmit
                ? t('wait-before-next-message', {
                    minutes: Math.floor(timeRemaining / 60),
                    seconds: (timeRemaining % 60).toString().padStart(2, '0'),
                  })
                : !formData.firstname.trim() ||
                  !formData.title.trim() ||
                  !formData.description.trim() ||
                  !formData.email.trim()
                ? t('all-fields-required')
                : t('contact-form-error')}
            </p>

            <div className='flex justify-end'>
              <button
                onClick={handleCloseStatusModal}
                className='bg-anthracite text-white px-4 py-2 rounded hover:bg-opacity-90'
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ContactFormAuthenticated
